"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, FileSearch, Sparkles, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const guarantees = [{
  title: "Flexible monthly retainers",
  description: "Rolling month-to-month agreements with no lock-in contracts, clear sprint scopes, and direct consultant delivery.",
  icon: UserCheck
}, {
  title: "Custom growth strategy",
  description: "Tailored roadmaps crafted for sustainable organic revenue and market visibility.",
  icon: CheckCircle2
}, {
  title: "Transparent reporting",
  description: "Clear deliverables, measurable search metrics, and honest commercial impact.",
  icon: Sparkles
}];
export default function FinalCta() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <section id="finalcalltoaction" data-nav="light" className="relative overflow-hidden bg-background py-20 md:py-32">
      {/* Subtle decorative background glow */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center opacity-30">
        <div className="h-96 w-96 rounded-full bg-secondary blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mx-auto max-w-5xl">
          <Card className="relative overflow-hidden border-border bg-card shadow-xl">
            {/* Top highlight bar */}
            <div className="h-1.5 w-full bg-primary" />

            <CardContent className="p-8 sm:p-12 lg:p-16">
              <div className="mx-auto max-w-3xl text-center">
                <motion.div variants={itemVariants} className="flex justify-center">
                  <Badge variant="secondary" className="mb-6 px-3.5 py-1 text-sm font-medium tracking-normal text-primary">
                    Direct Consultant Partnership
                  </Badge>
                </motion.div>

                <motion.h2 variants={itemVariants} className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                  Ready to grow your organic traffic? Let&apos;s talk.
                </motion.h2>

                <motion.p variants={itemVariants} className="mt-6 text-lg leading-relaxed text-muted-foreground sm:text-xl">
                  My monthly retainers run on flexible rolling agreements with dedicated sprint
                  deliverables, continuous technical optimisation, and transparent monthly reviews.
                  Every engagement operates on a clear month-to-month basis, giving you total freedom to adapt priorities, scale effort, or pause anytime without tie-ins.
                </motion.p>

                {/* Primary and secondary CTAs */}
                <motion.div variants={itemVariants} className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-5">
                  <Button asChild size="lg" className="w-full bg-primary font-medium text-primary-foreground shadow-md transition-colors hover:bg-primary/90 sm:w-auto">
                    <Link to="/ContactPage" className="inline-flex items-center gap-2">
                      Book a Free SEO Consultation
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>

                  <Button asChild variant="outline" size="lg" className="w-full border-border bg-background font-medium text-foreground transition-colors hover:bg-secondary hover:text-secondary-foreground sm:w-auto">
                    <Link to="/SeoAuditsPage" className="inline-flex items-center gap-2">
                      <FileSearch className="size-4 text-accent" />
                      Get a Free SEO Audit
                    </Link>
                  </Button>
                </motion.div>

                {/* Key partnership pillars */}
                <motion.div variants={itemVariants} className="mt-12 grid grid-cols-1 gap-6 border-t border-border pt-10 text-left sm:grid-cols-3">
                  {guarantees.map((item, i) => {
                  const IconComponent = item.icon;
                  return <div key={item.title} data-index={i} className="flex flex-col gap-2 rounded-lg bg-background/50 p-4 border border-border/50">
                        <div className="flex items-center gap-2 text-primary">
                          <IconComponent className="size-5 shrink-0 text-accent" />
                          <span className="font-semibold text-foreground text-sm">
                            {item.title}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </div>;
                })}
                </motion.div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}